import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { getQueryKey } from '@/feature/utils';
import { CurrentUser, RoleType } from '@feature/security';

import { ChangePasswordRequest, LoginRequest } from './LoginTypes';
import * as api from './SecurityApi';

export const securityKey = getQueryKey('security');

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<unknown, AxiosError, LoginRequest>({
    mutationFn: (payload: LoginRequest) => api.login(payload),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: securityKey.root(),
      });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => queryClient.resetQueries(),
  });
}

export function getCurrentUserOptions() {
  return {
    queryKey: securityKey.as(['login']),
    queryFn: () => api.getCurrentUser(),
  };
}

export function useCurrentUserQuery() {
  return useQuery(getCurrentUserOptions());
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation<unknown, AxiosError, ChangePasswordRequest>({
    mutationFn: (payload: ChangePasswordRequest) => api.changePassword(payload),
    // The route guards read the cached user through ensureQueryData, so a failed refetch must not leave the flag set.
    onSuccess: () => {
      queryClient.setQueryData<CurrentUser>(
        getCurrentUserOptions().queryKey,
        user => user && { ...user, passwordChangeRequired: false },
      );
      return queryClient.invalidateQueries({ queryKey: securityKey.root() });
    },
  });
}

export function useChangeCurrentRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleType: RoleType) => api.changeCurrentRole(roleType),
    // invalidate all queries on role change
    onSuccess: () => {
      return queryClient.resetQueries();
    },
  });
}
